// Solo Leveling-style RPG math + helpers

export const RANKS = [
  { code: "E", name: "Awakened", color: "#64748B", minLevel: 1 },
  { code: "D", name: "Apprentice", color: "#10B981", minLevel: 5 },
  { code: "C", name: "Adept", color: "#3B82F6", minLevel: 10 },
  { code: "B", name: "Veteran", color: "#8B5CF6", minLevel: 18 },
  { code: "A", name: "Elite", color: "#F59E0B", minLevel: 28 },
  { code: "S", name: "Sovereign", color: "#EF4444", minLevel: 40 },
  { code: "SS", name: "Monarch", color: "#EC4899", minLevel: 55 },
  { code: "SSS", name: "Shadow Monarch", color: "#FFD700", minLevel: 75 },
];

// XP needed to go FROM level L to L+1
export const xpForLevel = (level) =>
  Math.floor(80 * level + 20 * level * level);

export const totalXpForLevel = (level) => {
  let total = 0;
  for (let i = 1; i < level; i++) total += xpForLevel(i);
  return total;
};

export const getRank = (level) => {
  let current = RANKS[0];
  for (const r of RANKS) {
    if (level >= r.minLevel) current = r;
  }
  return current;
};

export const getNextRank = (level) => {
  for (const r of RANKS) {
    if (level < r.minLevel) return r;
  }
  return null;
};

// Apply xp gain, returns { level, xp, leveledUp, rankedUp, oldLevel, oldRank, newRank }
export const applyXpGain = (state, gain) => {
  let { level, xp } = state;
  const oldLevel = level;
  const oldRank = getRank(level);
  xp += gain;
  let leveledUp = false;
  while (xp >= xpForLevel(level)) {
    xp -= xpForLevel(level);
    level += 1;
    leveledUp = true;
  }
  const newRank = getRank(level);
  const rankedUp = newRank.code !== oldRank.code;
  return { level, xp, leveledUp, rankedUp, oldLevel, oldRank, newRank };
};

export const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const yesterdayKey = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const daysBetween = (k1, k2) => {
  const a = new Date(k1);
  const b = new Date(k2);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

export const weekDates = () => {
  // Returns 7 day-keys ending today, oldest first
  const arr = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    arr.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      label: d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1),
      dayNum: d.getDate(),
      isToday: i === 0,
    });
  }
  return arr;
};

export const DEFAULT_QUESTS = [
  { id: "q1", title: "30 Push-Ups", xp: 50, icon: "Dumbbell" },
  { id: "q2", title: "30 Sit-Ups", xp: 50, icon: "Activity" },
  { id: "q3", title: "30 Squats", xp: 50, icon: "PersonStanding" },
  { id: "q4", title: "10km Run", xp: 100, icon: "Footprints" },
  { id: "q5", title: "15 min Stretching", xp: 30, icon: "Wind" },
];

export const PRESET_AVATARS = [
  {
    id: "male_hunter",
    name: "Hunter",
    url: "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/3c1f674446fd20407c52235243696acc65de62969bb4584c2dec8a3635aefdf7.png",
  },
  {
    id: "female_hunter",
    name: "Valkyrie",
    url: "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/5a33137d3c8898373c5cd6d84ba0412e3cf765fbc652612b320b7ed7200ea0be.png",
  },
  {
    id: "shadow_knight",
    name: "Shadow Knight",
    url: "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/1a8165d3d710c9d00c9bbe2e6ce0bc877575fee2d51eaee865326042167efda1.png",
  },
  {
    id: "mage",
    name: "Arcane Mage",
    url: "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/20a6b24c40c234d7c37a99acd11b87f9f47e6801cd072f6183e63f38db6f59a3.png",
  },
];

export const LEVEL_UP_BG =
  "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/fbc35d3d3de390b04b4de9660b9874b7647ee719216b50b9d616adbe52d2d081.png";

export const RANK_UP_BG =
  "https://static.prod-images.emergentagent.com/jobs/c3fbd1c2-69a6-4990-87ad-26dca60840f3/images/fb5ce9c47beaecc8a6a15bfa452cb660345b942979b2e240092453af76ea72bf.png";

// Achievements / Titles. `check(state)` returns true if unlocked.
export const ACHIEVEMENTS = [
  {
    id: "first_blood",
    title: "First Blood",
    desc: "Complete your first day of quests.",
    icon: "Swords",
    check: (s) => s.totalWorkouts >= 1,
  },
  {
    id: "iron_resolve",
    title: "Iron Resolve",
    desc: "Maintain a 3-day streak.",
    icon: "Flame",
    check: (s) => (s.bestStreak ?? s.streak) >= 3,
  },
  {
    id: "week_warrior",
    title: "Week Warrior",
    desc: "Maintain a 7-day streak.",
    icon: "Calendar",
    check: (s) => (s.bestStreak ?? s.streak) >= 7,
  },
  {
    id: "unbroken",
    title: "Unbroken",
    desc: "Maintain a 30-day streak.",
    icon: "Shield",
    check: (s) => (s.bestStreak ?? s.streak) >= 30,
  },
  {
    id: "quest_forger",
    title: "Quest Forger",
    desc: "Create your first custom quest.",
    icon: "Hammer",
    check: (s) => !!s.customQuestCreated,
  },
  {
    id: "centurion",
    title: "Centurion",
    desc: "Complete 100 quests.",
    icon: "Trophy",
    check: (s) => s.totalWorkouts >= 100,
  },
  {
    id: "millennial",
    title: "Millennial",
    desc: "Earn 1,000 total XP.",
    icon: "Star",
    check: (s) => s.totalXp >= 1000,
  },
  {
    id: "adamant",
    title: "Adamant",
    desc: "Earn 10,000 total XP.",
    icon: "Gem",
    check: (s) => s.totalXp >= 10000,
  },
  {
    id: "rank_d",
    title: "Awakened Apprentice",
    desc: "Reach D-Rank.",
    icon: "ChevronsUp",
    check: (s) => s.level >= 5,
  },
  {
    id: "rank_c",
    title: "Tactical Adept",
    desc: "Reach C-Rank.",
    icon: "ChevronsUp",
    check: (s) => s.level >= 10,
  },
  {
    id: "rank_b",
    title: "Veteran Hunter",
    desc: "Reach B-Rank.",
    icon: "ChevronsUp",
    check: (s) => s.level >= 18,
  },
  {
    id: "rank_a",
    title: "Elite Operative",
    desc: "Reach A-Rank.",
    icon: "Crown",
    check: (s) => s.level >= 28,
  },
  {
    id: "rank_s",
    title: "Sovereign Ascendant",
    desc: "Reach S-Rank.",
    icon: "Crown",
    check: (s) => s.level >= 40,
  },
  {
    id: "rank_ss",
    title: "Monarch's Chosen",
    desc: "Reach SS-Rank.",
    icon: "Crown",
    check: (s) => s.level >= 55,
  },
  {
    id: "rank_sss",
    title: "Shadow Monarch",
    desc: "Reach SSS-Rank.",
    icon: "Crown",
    check: (s) => s.level >= 75,
  },
];

export const checkNewAchievements = (state) => {
  const unlocked = state.achievements || {};
  const newly = [];
  for (const a of ACHIEVEMENTS) {
    if (!unlocked[a.id] && a.check(state)) newly.push(a);
  }
  return newly;
};
